import { Form, Flex, Button, BUTTON_TYPES, mediaMixin, spaceMixin } from '@zillow/constellation';
import { useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { useState, useEffect, useCallback } from 'react';
import { Outputs } from './outputs';
import { INPUTS } from './constants';
import { AnnualIncomeField, CreditSelectField, DownPaymentField, MonthlyDebtField, LocationField } from './form-fields';
import { performCalculation } from './api';
import useCalculatorContext from './useCalculatorContext';
import {
    trackGACalculatorOutcome,
    trackGACalculatorVisible,
    trackGAFieldFocus,
    trackGAMortgageEvent,
} from './analytics';

const Wrapper = styled(Form)`
    display: flex;
    flex-direction: column;
`;

const FieldsWrapper = styled(Flex)`
    ${mediaMixin(
        'lg',
        css`
            max-width: 375px;
            width: 375px;
            padding-right: ${spaceMixin('md')};
        `
    )}
`;

const CalculatorForm = () => {
    const { outputs, updateOutputs } = useCalculatorContext();
    const [isCalculating, setIsCalculating] = useState(false);
    const {
        formState: { errors, isValid },
        register,
        setValue,
        trigger,
        clearErrors,
        watch,
        handleSubmit,
    } = useForm({
        mode: 'onBlur',
        shouldUnregister: true,
    });

    const cityOrZip = watch(INPUTS.cityOrZip, '');
    const disabled = !isValid || isCalculating;

    useEffect(() => {
        trackGACalculatorVisible();
    }, []);

    const handleFocus = useCallback(e => trackGAFieldFocus(e.target?.id), []);
    const handleLocationFocus = useCallback(() => trackGAFieldFocus(INPUTS.cityOrZip), []);

    const onSubmit = useCallback(
        async (inputFields, e) => {
            e.preventDefault();
            setIsCalculating(true);
            trackGAMortgageEvent('Budget Calculator: Interaction', 'Calculate Button');
            try {
                const result = await performCalculation(inputFields);
                trackGACalculatorOutcome(result);
                updateOutputs({
                    inputFields,
                    ...result,
                });
            } catch (err) {
                trackGAMortgageEvent(
                    'Budget Calculator: Interaction',
                    `Calculator Error: "${JSON.stringify(inputFields)}" | error: "${JSON.stringify(err)}"`
                );
            }
            setIsCalculating(false);
        },
        [updateOutputs]
    );

    return (
        <>
            <Wrapper onSubmit={handleSubmit(onSubmit)}>
                <Flex display="flex" flexDirection={{ default: 'column', lg: 'row' }} justifyContent="center">
                    <FieldsWrapper display="flex" flexDirection="column" flex="1">
                        <AnnualIncomeField
                            register={register}
                            name={INPUTS.annualIncome}
                            error={errors?.annualIncome}
                            onFocus={handleFocus}
                        />
                        <MonthlyDebtField
                            register={register}
                            name={INPUTS.monthlyDebts}
                            error={errors?.monthlyDebts}
                            onFocus={handleFocus}
                        />
                        <DownPaymentField
                            register={register}
                            name={INPUTS.downPayment}
                            error={errors?.downPayment}
                            onFocus={handleFocus}
                        />
                        <CreditSelectField register={register} name={INPUTS.creditScore} onFocus={handleFocus} />
                        <LocationField
                            value={cityOrZip}
                            register={register}
                            setValue={setValue}
                            errors={errors}
                            trigger={trigger}
                            clearErrors={clearErrors}
                            onFocus={handleLocationFocus}
                        />
                    </FieldsWrapper>
                    <Flex display="flex" flex="1" justifyContent="center" marginTop={{ default: 'md', lg: 0 }}>
                        <Outputs outputs={outputs} isCalculating={isCalculating} />
                    </Flex>
                </Flex>
                <Button buttonType={BUTTON_TYPES.secondary} marginTop="lg" fluid disabled={disabled}>
                    Calculate
                </Button>
            </Wrapper>
        </>
    );
};

export default CalculatorForm;
